import { Component, inject } from '@angular/core';
import { RouterOutlet, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Toast } from 'primeng/toast';
import { NavigationComponent } from './components/navigation/navigation.component';
import { SideNavComponent } from './components/navigation/side-nav/side-nav.component';
import { AuthService } from './services/auth.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, CommonModule, Toast, NavigationComponent, SideNavComponent],
  template: `
    <p-toast position="top-right"></p-toast>
    @if (!shouldHideNav()) {
      <div class="app-shell">
        <!-- Sidebar: sticky on desktop, fixed overlay on mobile -->
        <aside class="app-sidebar" [class.collapsed]="sidebarCollapsed">
          <app-side-nav (navigated)="onNavNavigated()" (toggleSidebar)="onMenuToggle()"></app-side-nav>
        </aside>

        <!-- Mobile backdrop: click outside to close -->
        @if (!sidebarCollapsed) {
          <div class="mobile-backdrop" (click)="onMenuToggle()"></div>
        }

        <div class="app-main">
          <app-navigation (menuToggle)="onMenuToggle()" [navOpen]="navOpen"></app-navigation>
          <router-outlet></router-outlet>
        </div>
      </div>
    } @else {
      <router-outlet></router-outlet>
    }
  `,
  styles: [`
    :host { display:block; min-height:100vh; }

    .app-shell {
      display:flex;
      min-height:100vh;
      --sidebar-w:260px;
    }
    .app-shell:has(.app-sidebar.collapsed) { --sidebar-w:0px; }

    /* Desktop sidebar: persistent, pushes content */
    .app-sidebar {
      width:260px;
      flex-shrink:0;
      height:100vh;
      position:sticky;
      top:0;
      overflow-y:auto;
      border-right:1px solid #e5e7eb;
      background:#fff;
      transition:width 0.25s ease;
      z-index:100;
    }
    .app-sidebar.collapsed {
      width:0;
      overflow:hidden;
      border-right:none;
    }

    /* min-width:0 lets this flex child shrink so inner horizontal-scroll areas keep working */
    .app-main { flex:1; min-width:0; }

    /* Mobile sidebar: fixed overlay, slides in/out */
    @media (max-width: 1023px) {
      .app-shell { --sidebar-w:0px; }
      .app-sidebar {
        position:fixed; left:0; top:0; height:100vh; width:260px;
        transition:transform 0.25s ease; transform:translateX(0); overflow-y:auto;
      }
      .app-sidebar.collapsed {
        width:260px; overflow:hidden;
        border-right:1px solid #e5e7eb;
        transform:translateX(-260px);
      }
    }

    /* Mobile backdrop */
    .mobile-backdrop { display:none; }
    @media (max-width: 1023px) {
      .mobile-backdrop {
        display:block; position:fixed; inset:0;
        background:rgba(0,0,0,0.35); z-index:99;
      }
    }
  `]
})
export class AppComponent {
  private router = inject(Router);
  private authService = inject(AuthService);

  sidebarCollapsed = false;
  navOpen = true;

  onMenuToggle() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    this.navOpen = !this.sidebarCollapsed;
  }

  // Close sidebar when a nav item is clicked on mobile.
  onNavNavigated() {
    if (window.innerWidth < 1024) {
      this.sidebarCollapsed = true;
      this.navOpen = false;
    }
  }

  shouldHideNav(): boolean {
    const url = this.router.url;
    const onAuthRoute = url === '/signin' || url === '/signup' || url === '/';
    return onAuthRoute || !this.authService.isLoggedIn();
  }
}
